/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import PaymentsIcon from '@mui/icons-material/Payments'
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Radio,
  Stack,
  Tooltip,
  Typography,
  styled
} from '@mui/material'
import { InvoiceCloudFrame, MButton, ProgressLoaders, Text } from 'components'
import QuickLinks from 'components/layouts/AuthorizedLayout/component/QuickLinks'
import { CONTACT_PHONE_2, MESSAGES, USER_TOKEN } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { APIs } from 'services'
import { formatValue, useLocalStorage } from 'utils'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { ReactComponent as RuggedBottom } from 'assets/icons/RuggedBottom.svg'
import { EditInformationForm, type IMailingAddress } from './component/Form'
import LinkAccount from './component/LinkAccount'

export enum LinkAccountMode {
  LINK = 'LINK',
  UNLINK = 'UNLINK'
}

export interface IUserValue {
  name?: string
  officeNumber?: string
  mobileNumber?: string
  mailingAddress?: string
  email?: string
  balance?: string
  alteredEmail?: string
  serviceAddress?: string[]
}

export interface ShowLinkAccountType {
  open: boolean
  mode: LinkAccountMode.LINK | LinkAccountMode.UNLINK
  accountId: string
}

export const Account: React.FC = () => {
  const { account, billing, isAdmin, userEmail, currentAccount, getAccount, handleCurrentAccount, getBilling, updateMailAddress } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [active, setActive] = useState<number>(0)
  const [userValue, setUserValue] = useState<IUserValue>()
  const [defaultAccountId, setDefaultAccountId] = useState<any>(null)
  const isMobile = useMobileBreakpoints()
  const billURL = billing?.billing[billing.billing.length - 1]?.billDisplayURL ?? null
  const [prevAccountId, setPrevAccountId] = useState<string>('')
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [mailingAddress, setMailingAddress] = useState<IMailingAddress>()
  const [portalPhone, setPortalPhone] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showLinkAccount, setShowLinkAccount] = useState<ShowLinkAccountType>({ open: false, mode: LinkAccountMode.LINK, accountId: '' })

  useEffect(() => {
    if (prevAccountId === currentAccount?.accountId || currentAccount?.accountId === account?.myAccount?.accountId) {
      return
    }
    if (currentAccount?.accountId !== null) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin,
        email: userEmail
      })
    }
    setPrevAccountId((currentAccount?.accountId ?? account?.myAccount?.accountId) ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentAccount])

  useEffect(() => {
    if (account) {
      const serviceAddresses = account?.myAccount?.serviceAddresses.map(item => item.serviceAddress)
      setUserValue({
        name: account?.myAccount?.personName,
        officeNumber: account?.myAccount?.alternateNumber,
        mobileNumber: account?.myAccount?.mobileNumber,
        mailingAddress: `${account?.myAccount?.mailingAddress?.addressLine1}, ${account?.myAccount?.mailingAddress?.city}, ${account?.myAccount?.mailingAddress?.state} ${account?.myAccount?.mailingAddress?.postal}`,
        email: account?.myAccount?.email,
        balance:
          account?.myAccount?.accountBalance &&
          formatValue(account?.myAccount.accountBalance),
        alteredEmail: 'ND',
        serviceAddress: serviceAddresses
      })
    }
    if (!billing) {
      void getBilling({
        AccessToken: authToken,
        accountId: account?.myAccount.accountId,
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        admin: isAdmin,
        email: userEmail
      })
    }
  }, [account, billing])

  useEffect(() => {
    if (account && !defaultAccountId) {
      setDefaultAccountId(account?.myAccount.defaultAccountId)
    }
  }, [account, defaultAccountId])

  const handleLinkAccountClick = ({ open, mode, accountId }: ShowLinkAccountType) => {
    setShowLinkAccount({ open, mode, accountId })
  }

  const handleCheckBox = async (item: any): Promise<void> => {
    try {
      const body: any = {
        AccessToken: authToken,
        accountId: account?.myAccount.accountId,
        admin: isAdmin,
        email: userEmail
      }
      body.updates = [
        {
          object: 'DEFAULTACCOUNT',
          value: item.accountId
        }
      ]
      await APIs.updateUser(body)
      enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_SUCCESS, {
        variant: 'success',
        autoHideDuration: 3000
      })
      setDefaultAccountId(item.accountId)
      handleCurrentAccount({ accountId: item.accountId ?? '', premiseId: item?.premiseId ?? '' })
      void getAccount({
        AccessToken: authToken,
        accountId: item.accountId
      })
    } catch (e) {
      enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_ERROR, {
        variant: 'error',
        autoHideDuration: 3000
      })
    }
  }


  const handleEdit = async (): Promise<void> => {
    setIsLoading(true)

    if (isEditing && mailingAddress) {
      try {
        const response = await updateMailAddress({
          AccessToken: authToken,
          addressLine1: mailingAddress.addressLine1,
          addressLine2: mailingAddress.addressLine2,
          postal: mailingAddress.postal,
          city: mailingAddress.city,
          state: mailingAddress.state
        })
        if (response && response?.status === 'success') {
          enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_SUCCESS, {
            variant: 'success',
            autoHideDuration: 3000
          })
          setUserValue({
            ...userValue,
            mailingAddress: `${mailingAddress.addressLine1}, ${mailingAddress.city}, ${mailingAddress.state}, - ${mailingAddress.postal}`
          })
        }
      } catch (e: any) {
        if (e && e?.response?.data?.code === 'UPDATE_LIMIT_EXCEEDED') {
          enqueueSnackbar(MESSAGES.UPDATE_LIMIT_EXCEEDED, {
            variant: 'error',
            autoHideDuration: 3000
          })
        } else {
          enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_ERROR, {
            variant: 'error',
            autoHideDuration: 3000
          })
        }
      }
    }

    if (isEditing && portalPhone) {
      try {
        const response = await APIs.updateUser({
          AccessToken: authToken,
          accountId: account?.myAccount.accountId ?? '',
          admin: isAdmin,
          email: userEmail,
          updates: [
            {
              object: 'PORTALPHONE',
              value: portalPhone
            }
          ]
        })
        if (response.status === 1) {
          enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_SUCCESS, {
            variant: 'success',
            autoHideDuration: 3000
          })
          setUserValue({
            ...userValue,
            mobileNumber: portalPhone
          })
        }
      } catch (e) {
        enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_ERROR, {
          variant: 'error',
          autoHideDuration: 3000
        })
      }
    }

    setIsLoading(false)
    setIsEditing(!isEditing)
  }

  const handleMailingAddressChange = (mailingAddress: IMailingAddress): void => {
    setMailingAddress(mailingAddress)
  }

  const handlePortalPhoneChange = (portalPhone: string): void => {
    setPortalPhone(portalPhone)
  }

  return (
    <>
      {isMobile && <QuickLinks />}
      <LinkAccount open={showLinkAccount.open} accountId={showLinkAccount.accountId} mode={showLinkAccount.mode} handleClose={() => { handleLinkAccountClick({ open: !showLinkAccount.open, mode: showLinkAccount.mode, accountId: showLinkAccount.accountId }) }} />
      {account !== null
        ? <Grid container spacing={2} mt={0}>
          <Grid item sm={10} md={8} >
            <Wrapper gap={2}>
              <BoxWrapper>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Typography color={color.grey[900]} fontSize={isMobile ? 18 : 24} fontWeight="600">
                    General Information
                  </Typography>
                  {!isMobile && <MButton
                    rounded="true"
                    variant={isEditing ? 'contained' : 'outlined'}
                    texttransform="none"
                    onClick={handleEdit}
                    startIcon={
                      isLoading && (
                        <CircularProgress color="inherit" size={20} />
                      )
                    }
                  >
                    {isEditing ? 'Save Changes' : 'Edit Information'}
                  </MButton>}

                </Box>

                {userValue
                  ? (
                    isEditing
                      ? <EditInformationForm
                        onMailingAddressChange={handleMailingAddressChange}
                        onPortalPhoneChange={handlePortalPhoneChange}
                        userValue={userValue}
                      />

                      : (
                        <GeneralInformation userValue={userValue} />
                      )
                  )
                  : (
                    <ProgressLoaders height="100%" />
                  )}
              </BoxWrapper>
              <BoxWrapper>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography color={color.grey[900]} fontSize={isMobile ? 18 : 24} fontWeight={600}>
                    Service Address
                  </Typography>
                  {/* <MButton disabled variant="outlined" rounded="true" texttransform="none" onClick={() => { handleLinkAccountClick({ open: !showLinkAccount.open, mode: LinkAccountMode.LINK, accountId: showLinkAccount.accountId }) }}>Link Account</MButton> */}
                </Box>

                <Box my={2}>
                  <Typography color={color.grey[800]} fontSize={16} fontWeight={400}>
                    Select your primary account. This will show by default when you log into the portal. If there are additional service addresses that are not listed below please contact:
                  </Typography>
                  <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={1} my={2}>
                    <Box flex={1} textAlign="center" bgcolor={color.background[50]} py={1}>
                      <Typography fontSize={16} color={color.grey[900]}>Call Center/Operator</Typography>
                      <Typography fontSize={16} color={color.primary[700]}>{CONTACT_PHONE_2}</Typography>
                    </Box>
                    <Box flex={1} textAlign="center" bgcolor={color.background[50]} py={1}>
                      <Typography fontSize={16} color={color.grey[900]}>Call Center/Operator</Typography>
                      <Typography fontSize={16} color={color.primary[700]}>{CONTACT_PHONE_2}</Typography>
                    </Box>
                    <Box flex={1} textAlign="center" bgcolor={color.background[50]} py={1}>
                      <Typography fontSize={16} color={color.grey[900]}>Call Center/Operator</Typography>
                      <Typography fontSize={16} color={color.primary[700]}>{CONTACT_PHONE_2}</Typography>
                    </Box>
                  </Box>
                </Box>

                <Box mb={2}>
                  <Box display="flex" alignItems="center" gap={0.5} p={1} bgcolor={color.background[100]}>
                    <WarningRoundedIcon fontSize="small" color="warning" />
                    <Typography fontSize={14} fontWeight={600}>Service Account Information</Typography>
                  </Box>
                  <Box p={1} bgcolor={color.background[50]}>
                    <Typography fontSize={12} color={color.grey[700]}>Delco Waters provides Customers with all account information and meter readings.
                      For this reason, all ESC Partners account holders must also have an account in order for customers to view customer services and data.
                      To start or stop service, click on service to submit a form.
                      To make changes to your name or mailing address on accounts with active service, click on My account.</Typography>
                  </Box>
                </Box>

                {account?.myAccount?.serviceAddresses?.map(
                  (item: any, index: number) => (
                    <Box
                      key={index}
                      border={`solid 1px ${color.white}`}
                      onClick={() => { setActive(0) }}
                    >
                      <Box
                        p={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor={active === 0 ? color.background[50] : color.white}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Radio
                            onChange={async () => { await handleCheckBox(item) }}
                            checked={item?.accountId === defaultAccountId && true}
                            disabled={!item?.legacyAcctNumber}
                          />
                          <Box>
                            <Typography fontSize={10} color={color.grey[700]} letterSpacing={1}>
                              ACCOUNT NUMBER
                            </Typography>
                            <Typography fontSize={16} color={color.grey[900]}>
                              {item?.accountId}
                            </Typography>
                          </Box>
                        </Stack>
                        {/* {!item?.legacyAcctNumber && <MButton variant="outlined" rounded="true" texttransform="none" onClick={() => { handleLinkAccountClick({ open: !showLinkAccount.open, mode: LinkAccountMode.UNLINK, accountId: item?.accountId }) }}>Unlink Account</MButton>} */}
                      </Box>
                      {isMobile
                        ? <Box p={1}>
                          <Box display="flex" flexDirection="column" mb={2}>
                            <Typography flex={1} color={color.grey[700]} fontSize={10} letterSpacing={1}>
                              PROPERTY ADDRESS
                            </Typography>
                            <Typography flex={1} color={color.grey[800]} fontSize={16}>
                              {item?.serviceAddress}
                            </Typography>
                          </Box>
                          {item?.legacyAcctNumber && <Box display="flex" flexDirection="column" >
                            <Typography flex={1} color={color.grey[700]} fontSize={10} letterSpacing={1}>
                              OLD ACCOUNT NUMBER
                            </Typography>
                            <Typography flex={1} color={color.grey[800]} fontSize={16}>
                              {item.legacyAcctNumber}
                            </Typography>
                          </Box>}
                        </Box>
                        : <Box m={1}>
                          <Box display="flex" p={1} borderBottom={`1px solid ${color.background[50]}`}>
                            <Typography flex={1} color={color.grey[700]} fontSize={10} letterSpacing={1}>
                              PROPERTY ADDRESS
                            </Typography>
                            {item?.legacyAcctNumber &&
                              <Typography flex={1} color={color.grey[700]} fontSize={10} letterSpacing={1}>
                                OLD ACCOUNT NUMBER
                              </Typography>
                            }
                          </Box>
                          <Box display="flex" p={1}>
                            <Typography flex={1} color={color.grey[800]} fontSize={16}>
                              {item?.serviceAddress}
                            </Typography>
                            {item?.legacyAcctNumber && <Typography flex={1} color={color.grey[800]} fontSize={16}>
                              {item?.legacyAcctNumber}
                            </Typography>}
                          </Box>
                        </Box>}
                    </Box>
                  )
                )}
              </BoxWrapper>
            </Wrapper>
          </Grid>
          <Grid item sm={12} md={4}>
            <BoxWrapper>
              <Box display="flex" alignItems="center" mb={3}>
                <PaymentsIcon fontSize='medium' sx={{ color: color.secondary[500] }} />
                <Typography ml={1} color={color.grey[900]} fontSize={isMobile ? 18 : 24} fontWeight="600">
                  Current Bill Detail
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Text color={color.black} fontSize="18px">
                  Total Due
                </Text>
                <Box
                  p="12px 16px"
                  sx={{ background: color.white, borderRadius: '4px' }}
                >
                  <Text color={color.grey[900]} fontSize="24px" fontWeight="600">
                    {userValue?.balance}
                  </Text>
                </Box>
              </Box>
              <InvoiceCloudFrame page={1}>
                <MButton
                  fullWidth
                  rounded="true"
                  variant="contained"
                  texttransform='none'
                  size="large"
                >
                  Pay Bill
                </MButton>
              </InvoiceCloudFrame>
              <Typography color={color.grey[700]} fontSize={16} my={2} textAlign={isMobile ? 'center' : 'left'}>
                To download your invoice,&nbsp;
                <Tooltip
                  placement="top"
                  title={!billURL ? 'Invoice not available, please try again later.' : ''}
                >
                  <MButton
                    onClick={() => billURL ? window.open(billURL, '_blank') : undefined}
                    variant="text"
                    texttransform='none'
                    size="small"
                    bgcolor='none'
                    sx={{
                      color: billURL ? color.background[900] : color.grey[200],
                      '&:hover': {
                        background: 'none'
                      }
                    }}
                  >
                    Click Here.
                  </MButton>
                </Tooltip>
              </Typography>
              <Typography color={color.grey[700]} fontSize={14}>
                Did you know you can now pay your bill using Apple Pay, Google
                Pay, Venmo, or Paypal?
              </Typography>
              <Typography display="flex" color={color.grey[700]} fontSize={14} my={2} textAlign={isMobile ? 'center' : 'left'}>
                Need a Payment Plan? Contact us at
                <Link
                  ml={0.5}
                  href="mailto:information@delcowater.com"
                  target="_blank"
                  rel="noreferrer"
                  color={color.background[900]}
                  style={{ textDecoration: 'none' }}
                >
                  information@delcowater.com
                </Link>
                .
              </Typography>
            </BoxWrapper>
            <RuggedBottom width="100%" />
          </Grid>
        </Grid>
        : <Box mt={3}><ProgressLoaders height="100%" /></Box>}
    </>

  )
}

const GeneralInformation = ({ userValue }: { userValue: IUserValue }): JSX.Element => {
  return (
    <Grid borderRadius="4px" container spacing={2}>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            NAME
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue?.name}
          </Text>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            HOME / OFFICE NUMBER
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue && (userValue.mobileNumber ?? userValue.officeNumber)}
          </Text>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            MAILING ADDRESS
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue?.mailingAddress}
          </Text>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            EMAIL ID
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue?.email}
          </Text>
        </Box>
      </Grid>
    </Grid>
  )
}

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`

export const BoxWrapper = styled(Box)({
  background: color.white,
  borderRadius: '4px',
  padding: '24px'
})
